import * as _ from 'lodash';

import IOverlay = ZoneIQCalibration.IOverlay;
import { CalibrationUsesValModel } from '../calibration-uses-val.model';
import { CalibrationGeneralValue } from '../calibration-general-value.model';
import { CalibrationParkingValue } from '../calibration-parking-value.model';
import { CalibrationOfficeCommercialValue } from '../calibration-office-commercial-value.model';
import ICalibrationUsesVal = ZoneIQCalibration.ICalibrationUsesVal;
import { CalibrationBonusValue } from '../calibration-bonus-value.model';
import ICalibrationUsesLabel = ZoneIQCalibration.ICalibrationUsesLabel;
import { CalibrationSetModel } from '../calibration-set.model';
import { ZoningCodeModel } from '../..';
import { OverlayType } from '../../../../shared/enums/overlay-type.enum';
import { ICalibrationParkingModel } from 'src/app/interfaces/calibration-parking-model.interface';
import { IOverlayTypologyModel } from 'src/app/interfaces/overlay-typology-model.interface';
import { IOverlayModel } from 'src/app/interfaces/overlay-model.interface';
import { ICalibrationGeneralModel } from 'src/app/interfaces/calibration-general-model.interface';
import { ICalibrationBonusModel } from 'src/app/interfaces/calibration-bonus-model.interface';
import { ICalibrationOfficeCommercialModel } from 'src/app/interfaces/calibration-office-commercial-model.interface';
import ICalibrationSetAttributesStatistic = ZoneIQCalibration.ICalibrationSetAttributesStatistic;
import IAllowedUse = ZoneIQCalibration.IAllowedUse;
import { CalibrationSetType } from 'src/shared/enums/calibration-set-type';
import IZoneDescriptionImportValidationResult = ZoneIQCalibration.IZoneDescriptionImportValidationResult;
import IEntityTableFieldAttributeImportValidationResult =
    ZoneIQCalibration.IEntityTableFieldAttributeImportValidationResult;

export class OverlayModel extends CalibrationSetModel<IOverlay> implements IOverlayModel {
    Id: number;
    ZoningCodeId: number;
    RevisionId: number;
    Name: string;
    Description: string;
    Priority: number;
    ZoneCombinationId?: number;
    GridicsIdProximityType: number;
    ProximityConditionId?: number;
    IsZoneCombinationLinkEnabled: boolean;
    IsGeneralLinkEnabled: boolean;
    IsUsesLinkEnabled: boolean;
    IsParkingLinkEnabled: boolean;
    IsOfficeLinkEnabled: boolean;
    IsBonusLinkEnabled: boolean;
    IsApplicabilityLinkEnabled: boolean;
    GridicsOverlayId?: number;
    BuildingCalibrationType: number;
    BuildingTypologyMandatory?: boolean;
    Label: string;
    CloneId: number;

    CalibrationUsesVals?: CalibrationUsesValModel[];
    CalibrationGeneral?: ICalibrationGeneralModel;
    CalibrationParking?: ICalibrationParkingModel;
    CalibrationBonus?: ICalibrationBonusModel;
    CalibrationOfficeCommercial?: ICalibrationOfficeCommercialModel;
    OverlayTypology?: IOverlayTypologyModel[];

    importValidationResult?:
        | IZoneDescriptionImportValidationResult
        | IEntityTableFieldAttributeImportValidationResult;

    constructor(data: IOverlay) {
        super(data);
        Object.assign(
            this,
            _.pick(data, [
                'Id',
                'ZoningCodeId',
                'RevisionId',
                'Name',
                'Description',
                'Priority',
                'ZoneCombinationId',
                'GridicsIdProximityType',
                'ProximityConditionId',
                'IsZoneCombinationLinkEnabled',
                'IsGeneralLinkEnabled',
                'IsUsesLinkEnabled',
                'IsParkingLinkEnabled',
                'IsOfficeLinkEnabled',
                'IsApplicabilityLinkEnabled',
                'GridicsOverlayId',
                'BuildingCalibrationType',
                'BuildingTypologyMandatory',
                'OverlayTypology',
                'Label',
                'CloneId',
                'importValidationResult'
            ])
        );
        this._calibrationSetType = this.isBuildingTypology ? CalibrationSetType.TYPOLOGY : CalibrationSetType.OVERLAY;
        this.initIdentifier();
    }

    get isBuildingTypology() {
        return this.BuildingCalibrationType === OverlayType.BUILDING_TYPOLOGY;
    }

    static factory(
        data: IOverlay,
        overlayArr: IOverlay[],
        zoningCode?: ZoningCodeModel,
        clbSetAttrStat?: ICalibrationSetAttributesStatistic[]
    ): IOverlayModel {
        const overlay = new OverlayModel(data);

        overlay.initCalibrationUsesVals(
            data.CalibrationUsesVals,
            zoningCode.CalibrationUsesLabels,
            zoningCode.AllowedUses,
            zoningCode?.notAllowedUse?.Id
        );
        overlay.CalibrationGeneral = new CalibrationGeneralValue(data.CalibrationGeneral);
        overlay.CalibrationParking = new CalibrationParkingValue(data.CalibrationParking);
        overlay.CalibrationOfficeCommercial = new CalibrationOfficeCommercialValue(data.CalibrationOfficeCommercial);
        overlay.CalibrationBonus = new CalibrationBonusValue(data.CalibrationBonus);
        if (!overlay.isBuildingTypology) {
            overlay.OverlayTypology = overlay.initOverlayTypology(data.OverlayTypology, overlayArr, clbSetAttrStat);
            overlay.OverlayTypology.sort(this.sortingCurrentOverlayTypology);
        }

        if (_.get(clbSetAttrStat, 'length')) {
            const attrStat = clbSetAttrStat.find(
                stat => stat.ParentId === overlay.currentState.Id && stat.ParentType === overlay.calibrationSetType
            );
            overlay.hasComments = _.get(attrStat, 'hasComments', false);
            overlay.hasLinks = _.get(attrStat, 'hasLinks', false);
            overlay.hasAssumptions = _.get(attrStat, 'hasAssumptions', false);
        }

        return overlay;
    }

    static sortingCurrentOverlayTypology(a, b): number {
        return a.currentState.Name < b.currentState.Name ? -1 : a.currentState.Name > b.currentState.Name ? 1 : 0;
    }

    initCalibrationUsesVals(
        usesValsData: ICalibrationUsesVal[],
        usesLabels: ICalibrationUsesLabel[],
        allowedUses: IAllowedUse[],
        notAllowedUseId: number
    ) {
        super.initCalibrationUsesVals(usesValsData, usesLabels, allowedUses, notAllowedUseId, 'OverlayId');
    }

    deleteUseValue(useToDelete: ICalibrationUsesVal) {
        super.deleteUseValue(useToDelete, -1, 'OverlayId');
    }
}
